import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Guid, UserStatus } from '@lsq-frontend/remote-services/enterprise/common';
import { PersonDetail } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateFormat, forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { ResponsiveService } from '@lsq-frontend/common-ui';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';

@LifecycleAware()
@Component({
    selector: 'users-table',
    styleUrls: ['./users-table.component.scss'],
    template: `
        <lsq-data-table
            *ngIf="!(responsiveService.smBelow | async)"
            [dataSource]="dataSource"
            [layoutFixed]="true"
            [columnVisibilities]="{
                impersonateUser: isAdmin
            }"
        >
            <lsq-column
                [column]="{
                    sortable: false,
                    header: nameHeader,
                    key: 'lastName',
                    width: '25%'
                }"
            >
                <ng-template [custom-cell] let-row="row">
                    <div class="name-column-name">
                        <lsq-smart-truncate
                            [value]="row.firstName + ' ' + row.lastName"
                        ></lsq-smart-truncate>
                        <span *ngIf="row.userKey === currentUserKey"> (You)</span>
                    </div>
                    <div class="name-column-title text-truncate">{{ row.title }}</div>
                </ng-template>
            </lsq-column>
            <lsq-column
                [column]="{
                    sortable: false,
                    header: emailHeader,
                    key: 'emailAddress',
                    truncateTextCss: true,
                    width: '25%'
                }"
            >
            </lsq-column>
            <lsq-phone-column
                [column]="{
                    sortable: false,
                    header: phoneHeader,
                    key: 'phoneNumber',
                    width: '15%'
                }"
            >
            </lsq-phone-column>
            <lsq-column
                [column]="{
                    sortable: false,
                    header: latestActivityHeader,
                    key: 'userInvitedAt',
                    width: '20%'
                }"
            >
                <ng-template [custom-cell] let-row="row">
                    <ng-container
                        *ngIf="row.userStatus !== userStatus.Pending && row.latestActivity"
                    >
                        <div class="text-truncate">
                            {{ row.latestActivity | lsqDate: 'shortDate' }}
                            {{ atLabel }}
                            {{ row.latestActivity | date: dateFormat.ShortTime }}
                            ET
                        </div>
                    </ng-container>
                    <ng-container
                        *ngIf="row.userStatus === userStatus.Pending && row.userInvitedAt"
                    >
                        <lsq-smart-truncate
                            *ngIf="row.userInvitedAt"
                            i18n="Invitation sent date time label"
                            [value]="'Invite sent ' + (row.userInvitedAt | daysPassed)"
                            [popoverValue]="
                                (row.userInvitedAt | lsqDate: 'shortDate') +
                                ' ' +
                                atLabel +
                                ' ' +
                                (row.userInvitedAt | date: dateFormat.ShortTime) +
                                'ET'
                            "
                        >
                        </lsq-smart-truncate>
                        <lsq-button
                            appearance="transparent"
                            (buttonClick)="handleReinviteUser(row.userKey)"
                            i18n
                        >
                            Resend Invite
                        </lsq-button>
                    </ng-container>
                </ng-template>
            </lsq-column>
            <lsq-column
                [column]="{
                    header: actionsHeader,
                    sortable: false,
                    key: 'actions',
                    width: '10%'
                }"
            >
                <ng-template [custom-cell] let-row="row">
                    <div class="action-column-inner">
                        <lsq-button
                            *ngIf="row.userStatus !== userStatus.Inactive && canEditPermissions"
                            appearance="icon"
                            (buttonClick)="handleManageUser(row)"
                            class="e2e-manage-user-button"
                            [style.marginRight]="'2rem'"
                        >
                            <lsq-icon type="FaEdit"></lsq-icon>
                        </lsq-button>
                        <lsq-button
                            *ngIf="row.userKey !== currentUserKey"
                            appearance="icon"
                            class="e2e-toggle-activation-button"
                            (buttonClick)="handleActivationToggle(row)"
                        >
                            <lsq-icon
                                *ngIf="
                                    row.userStatus === userStatus.Active ||
                                    row.userStatus === userStatus.Pending
                                "
                                type="FaUserSlash"
                            ></lsq-icon>
                            <lsq-icon
                                *ngIf="row.userStatus === userStatus.Inactive"
                                type="FaUserPlus"
                            ></lsq-icon>
                        </lsq-button>
                    </div>
                </ng-template>
            </lsq-column>
            <lsq-column
                [column]="{
                    header: loginHeader,
                    sortable: false,
                    key: 'impersonateUser',
                    width: '5%'
                }"
            >
                <ng-template [custom-cell] let-row="row">
                    <div
                        class="action-column-inner"
                        *ngIf="row.userStatus !== userStatus.Inactive && allowImpersonate"
                    >
                        <lsq-button appearance="icon" (buttonClick)="handleImpersonateUser(row)">
                            <lsq-icon type="FaSignInAlt"></lsq-icon>
                        </lsq-button>
                    </div>
                </ng-template>
            </lsq-column>
        </lsq-data-table>

        <lsq-data-table-mobile *ngIf="responsiveService.smBelow | async" [dataSource]="dataSource">
            <ng-template #mobileCardHeader let-row="row">
                <div class="mtc-header-content">
                    <div class="mtc-header-title">{{ row.firstName }} {{ row.lastName }}</div>
                    <div class="mtc-header-sub-title">Email: {{ row.emailAddress }}</div>
                </div>
            </ng-template>
            <ng-template #mobileCardColumns let-row="row">
                <div class="card-item">
                    <div class="card-item-label">
                        {{ nameHeader }}
                    </div>
                    <div class="card-item-value">
                        <strong>{{ row.firstName }} {{ row.lastName }}</strong>
                        <div>
                            <small *ngIf="row.title">
                                {{ row.title }}
                            </small>
                        </div>
                    </div>
                </div>
                <div class="card-item">
                    <div class="card-item-label">
                        {{ emailHeader }}
                    </div>
                    <div class="card-item-value">
                        {{ row.emailAddress }}
                    </div>
                </div>
                <div class="card-item">
                    <div class="card-item-label">
                        {{ phoneHeader }}
                    </div>
                    <div class="card-item-value">
                        {{ row.phoneNumber | phone }}
                    </div>
                </div>
                <div class="card-item">
                    <div class="card-item-label">
                        {{ latestActivityHeader }}
                    </div>
                    <div class="card-item-value">
                        <ng-container
                            *ngIf="row.userStatus !== userStatus.Pending && row.latestActivity"
                        >
                            {{ row.latestActivity | lsqDate: 'shortDate' }}
                            {{ atLabel }}
                            {{ row.latestActivity | date: dateFormat.ShortTime }}
                            ET
                        </ng-container>
                        <ng-container
                            *ngIf="row.userStatus === userStatus.Pending && row.userInvitedAt"
                        >
                            <lsq-smart-truncate
                                *ngIf="row.userInvitedAt"
                                i18n="Invitation sent date time label"
                                [value]="'Invite sent ' + (row.userInvitedAt | daysPassed)"
                                [popoverValue]="
                                    (row.userInvitedAt | lsqDate: 'shortDate') +
                                    ' ' +
                                    atLabel +
                                    ' ' +
                                    (row.userInvitedAt | date: dateFormat.ShortTime) +
                                    'ET'
                                "
                            >
                            </lsq-smart-truncate>
                            <lsq-button
                                appearance="transparent"
                                (buttonClick)="handleReinviteUser(row.userKey)"
                                i18n
                            >
                                Resend Invite
                            </lsq-button>
                        </ng-container>
                    </div>
                </div>
                <div
                    class="card-item"
                    *ngIf="row.userStatus !== userStatus.Inactive && canEditPermissions"
                >
                    <div class="card-item-value">
                        <a class="text-uppercase font-weight-bold" (click)="handleManageUser(row)">
                            {{ getManageUserText(row.userKey) }}
                        </a>
                    </div>
                </div>
                <div class="card-item">
                    <div class="card-item-value">
                        <a
                            class="text-uppercase font-weight-bold secondary-link"
                            (click)="handleActivationToggle(row)"
                        >
                            {{
                                row.userStatus === userStatus.Inactive
                                    ? activateLink
                                    : row.userKey !== currentUserKey
                                    ? deactivateLink
                                    : ''
                            }}
                        </a>
                    </div>
                </div>
                <div
                    *ngIf="isAdmin && row.userStatus !== userStatus.Inactive && allowImpersonate"
                    class="card-item"
                >
                    <div class="card-item-value">
                        <a
                            class="text-uppercase font-weight-bold"
                            (click)="handleImpersonateUser(row)"
                        >
                            Login
                        </a>
                    </div>
                </div>
            </ng-template>
        </lsq-data-table-mobile>
    `
})
export class UsersTableComponent implements OnDestroy {
    @Input() public isAdmin = false;
    @Input() public dataSource: LsqListSource<PersonDetail>;
    @Input() public canEditPermissions? = false;
    @Input() public allowImpersonate? = true;

    @Output() public manageUser = new EventEmitter<PersonDetail>();
    @Output() public impersonateUser = new EventEmitter<PersonDetail>();
    @Output() public activateUser = new EventEmitter<PersonDetail>();
    @Output() public deactivateUser = new EventEmitter<PersonDetail>();
    @Output() public reinviteUser = new EventEmitter<PersonDetail>();

    public userStatus = UserStatus;
    public dateFormat = DateFormat;

    public nameHeader = this._i18n({
        value: 'Name',
        meaning: 'Name header'
    });
    public emailHeader = this._i18n({
        value: 'Email address',
        meaning: 'Email header'
    });
    public phoneHeader = this._i18n({
        value: 'Phone number',
        meaning: 'Phone number header'
    });
    public latestActivityHeader = this._i18n({
        value: 'Latest activity',
        meaning: 'Latest activity header'
    });
    public actionsHeader = this._i18n({
        value: 'Actions',
        meaning: 'Actions header'
    });
    public loginHeader = this._i18n({
        value: 'Login',
        meaning: 'Login header'
    });
    public atLabel = this._i18n({
        value: 'at',
        meaning: '"At" between date and time label'
    });

    public editUserLink = this._i18n({
        value: 'Edit User',
        meaning: 'Edit user link'
    });
    public myProfileLink = this._i18n({
        value: 'My Profile',
        meaning: 'My Profile link'
    });
    public deactivateLink = this._i18n({
        value: 'Deactivate',
        meaning: 'Deactivate link'
    });
    public activateLink = this._i18n({
        value: 'Activate',
        meaning: 'Activate link'
    });

    public currentUserKey: Guid;

    constructor(
        private _i18n: I18n,
        public responsiveService: ResponsiveService,
        private _userService: UserService
    ) {
        this._userService.user$.pipe(forLifeOf(this)).subscribe((user) => {
            this.currentUserKey = user?.userKey;
        });
    }

    public ngOnDestroy(): void {}

    public handleManageUser(personDetail: PersonDetail): void {
        this.manageUser.emit(personDetail);
    }
    public handleImpersonateUser(personDetail: PersonDetail): void {
        this.impersonateUser.emit(personDetail);
    }
    public handleReinviteUser(personDetail: PersonDetail): void {
        this.reinviteUser.emit(personDetail);
    }
    public handleActivationToggle(personDetail: PersonDetail): void {
        if (
            personDetail.userStatus === UserStatus.Active ||
            personDetail.userStatus === UserStatus.Pending
        ) {
            this.deactivateUser.emit(personDetail);
        } else {
            this.activateUser.emit(personDetail);
        }
    }

    public getManageUserText(userKey: string): string {
        if (userKey !== this.currentUserKey) {
            return this.editUserLink;
        } else {
            return !this.isAdmin ? this.myProfileLink : '';
        }
    }
}
